<ng-template
  let-extraClass="extraClass"
  #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Item'"
      [canGoBack]="true"
      [customBack]="customBack"
      [showLinkIcon]="true"
      [linkIcon]="'trash'"
      (linkAction)="deleteItem()" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper>

    @if (!loaded) {
      <!-- Skeleton while the item loads, mirroring the real layout so content swaps
           in without shifting: item card (40px category icon + name/model/quantity,
           then the icon+text detail rows), then Maintenance and Notes cards. -->
      <div class="restock-skeleton">
        <tidy-card>
          <div class="item-skel-head">
            <ion-skeleton-text [animated]="true" class="item-skel-thumb" />
            <div class="item-skel-head-lines">
              <ion-skeleton-text [animated]="true" class="restock-skel-name" />
              <ion-skeleton-text [animated]="true" class="restock-skel-text-sm" />
            </div>
          </div>
          @for (row of skeletonDetailRows; track row) {
            <div class="restock-line-row item-skel-row">
              <ion-skeleton-text [animated]="true" class="item-skel-icon" />
              <ion-skeleton-text [animated]="true" class="restock-skel-text" />
            </div>
          }
        </tidy-card>
        <tidy-card>
          <div class="restock-line-row item-skel-row">
            <ion-skeleton-text [animated]="true" class="item-skel-icon" />
            <ion-skeleton-text [animated]="true" class="restock-skel-name" />
          </div>
          <div class="restock-line-row item-skel-row">
            <ion-skeleton-text [animated]="true" class="item-skel-pill" />
            <ion-skeleton-text [animated]="true" class="item-skel-pill" />
          </div>
        </tidy-card>
        <tidy-card>
          <div class="restock-line-row item-skel-row">
            <ion-skeleton-text [animated]="true" class="item-skel-icon" />
            <ion-skeleton-text [animated]="true" class="restock-skel-name" />
          </div>
          <div class="restock-line-row item-skel-row">
            <ion-skeleton-text [animated]="true" class="restock-skel-text" />
          </div>
          <div class="restock-line-row item-skel-row">
            <ion-skeleton-text [animated]="true" class="item-skel-input" />
          </div>
        </tidy-card>
      </div>
    }

    @if (loaded) {
      @if (item?.current_quantity < item?.min_quantity) {
        <tidy-alert>
          <tidy-row class="vertical-align-center extra-bottom-padding">
            <tidy-image
              [src]="'assets/img/action-needed.svg'"
              class="title-size" />
            <tidy-text
              [header]="'Low Inventory'" />
          </tidy-row>
          <tidy-row>
            <tidy-text
              [body]="'The inventory of this item is currently low. We created a task for the operations team to restock per your settings.'" />
          </tidy-row>
        </tidy-alert>
      }
      @if (item?.photo_url) {
        <img [src]="item?.photo_url">
      }
      <tidy-card>
        <tidy-card-button class="arrow-to-top" (action)="goToEditItem()">
          <tidy-row class="no-padding extra-bottom-padding">
            <ion-row>
              @if (item?.model?.category?.default_icon_url) {
                <tidy-row class="vertical-align-center">
                  <tidy-image
                    [src]="item?.model?.category?.default_icon_url"
                    style="width:40px" />
                </tidy-row>
              }
              <ion-col>
                <tidy-row>
                  <tidy-text
                    [header]="item?.name" />
                </tidy-row>
                <tidy-row>
                  @if (item?.model?.model && item?.model?.model !== item?.name) {
                    <tidy-text
                      [body]="item?.model?.model" />
                  }
                </tidy-row>
                @if (item?.current_quantity) {
                  <tidy-row>
                    <tidy-text
                      [body]="('Current Quantity: ' | translate) + item?.current_quantity" />
                  </tidy-row>
                }
              </ion-col>
            </ion-row>
          </tidy-row>
          @if (item?.consumable) {
            <tidy-row class="extra-bottom-padding" />
            <tidy-row class="vertical-align-top extra-bottom-padding">
              <tidy-image
                [src]="'assets/img/inventory.svg'"
                class="title-size" />
              <tidy-text
                [body]="('Inventory Settings: ' | translate) + inventorySetting" />
            </tidy-row>
            @if (usedPerReservation !== null) {
              <tidy-row class="vertical-align-top extra-bottom-padding">
                <tidy-image
                  [src]="'assets/img/reservations.svg'"
                  class="title-size" />
                <tidy-text
                  [body]="('Used per Reservation: ' | translate) + usedPerReservation" />
              </tidy-row>
            }
          }
          @if (warehouseItem) {
            <tidy-row class="vertical-align-top extra-bottom-padding">
              <tidy-image
                [src]="'assets/img/home.svg'"
                class="title-size" />
              <tidy-text
                [body]="'Pulls from: '" />
              <tidy-text
                [actionBody]="warehouseItem?.address?.address_name || warehouseItem?.address?.address1"
                (action)="goToWarehouseItem()"
                class="link" />
            </tidy-row>
          }
          @if (item?.model?.characteristics[0]?.name) {
            <tidy-row class="vertical-align-top extra-bottom-padding">
              <tidy-image
                [src]="'assets/img/information.svg'"
                class="title-size" />
              <tidy-text
                [body]="getCharacteristics(item?.model?.characteristics)" />
            </tidy-row>
          }
          @if (item?.installation_date) {
            <tidy-row class="vertical-align-top extra-bottom-padding">
              <tidy-image
                [src]="'assets/img/calendar.svg'"
                class="title-size" />
              <tidy-text
                [body]="('Year Installed: ' | translate) + getInstallationYear(item?.installation_date)" />
            </tidy-row>
          }
          @if (item?.min_quantity) {
            <tidy-row class="vertical-align-top extra-bottom-padding">
              <tidy-image
                [src]="'assets/img/min-amount.svg'"
                class="title-size" />
              <tidy-text
                [body]="('Min Amount: ' | translate) + item?.min_quantity" />
            </tidy-row>
            <tidy-row class="vertical-align-top extra-bottom-padding">
              <tidy-image
                [src]="'assets/img/max-amount.svg'"
                class="title-size" />
              <tidy-text
                [body]="('Max Amount: ' | translate) + (item?.max_quantity ? (item?.max_quantity | translate) : ('no max' | translate))" />
            </tidy-row>
            @if (item?.opportunistic_quantity) {
              <tidy-row class="vertical-align-top extra-bottom-padding">
                <tidy-image
                  [src]="'assets/img/packages.svg'"
                  class="title-size" />
                <tidy-text
                  [body]="('Opportunistic Order Level: ' | translate) + item?.opportunistic_quantity + (' min (restock if other items are being restocked)' | translate)" />
              </tidy-row>
            }
            <tidy-row class="vertical-align-top extra-bottom-padding">
              <tidy-image
                [src]="'assets/img/best-price.svg'"
                class="title-size" />
              <tidy-text
                [body]="('Restock Price: $' | translate) + item?.min_restocking_price || 0 + (' min / ' | translate) + (item?.max_restocking_price ? '$' + (item?.max_restocking_price | translate) : ('no max' | translate))" />
            </tidy-row>
            @if (item?.where_to_order) {
              @if (integrationDetails) {
                <tidy-text
                  [body]="'Where to Order: '" />
                @if (integrationDetails.logo_url) {
                  <tidy-image
                    [src]="integrationDetails.logo_url"
                    style="width: 36px; height: 36px; margin-left: 8px; margin-right: 8px; vertical-align: middle; object-fit: contain;" />
                }
                <tidy-text
                  [body]="integrationDetails.name" />
              } @else {
                <tidy-text
                  [body]="('Where to Order: ' | translate) + item.where_to_order" />
              }
            }
            <!--<tidy-row class="vertical-align-middle extra-bottom-padding">
            <tidy-image
              [src]="'assets/img/credit-card.svg'"
              class="title-size">
            </tidy-image>
            <tidy-text
              [body]="'Payment Method: Card on file (9285)'">
            </tidy-text>
          </tidy-row>-->
          @if (item?.restocking_notes) {
            <tidy-row class="vertical-align-top extra-bottom-padding">
              <tidy-image
                [src]="'assets/img/delivery-man.svg'"
                class="title-size" />
              <tidy-text
                [body]="('Notes: ' | translate) + item.restocking_notes" />
            </tidy-row>
          }
          @if (item?.delivery_timing_preference) {
            <tidy-row class="vertical-align-top extra-bottom-padding">
              <tidy-image
                [src]="'assets/img/clock.svg'"
                class="title-size" />
              <tidy-text
                [body]="('Delivery Timing Preferences: ' | translate) + item?.delivery_timing_preference" />
            </tidy-row>
          }
          @if (displayBrands.length) {
            <tidy-row class="vertical-align-top extra-bottom-padding">
              <tidy-image
                [src]="'assets/img/star.svg'"
                class="title-size" />
              <tidy-text
                [body]="('Preferred Brands: ' | translate) + preferredBrandsSummary"
                [translate]="false" />
            </tidy-row>
          }
        }
      </tidy-card-button>
    </tidy-card>
      @if (currentOrder) {
        <tidy-card>
          <tidy-card-button class="arrow-to-top" (action)="goToCurrentOrder()">
            <tidy-row [alignLastItemRight]="true" class="vertical-align-center extra-bottom-padding">
              <tidy-image
                [src]="'assets/img/restock.svg'"
                class="header-size" />
              <tidy-text
                [header]="'Current Order'" />
              <span [class]="currentOrder.chipClass">{{ currentOrder.chipLabel | translate }}</span>
            </tidy-row>
            <div class="restock-item-stepper">
              @for (step of currentOrder.steps; track step.label) {
                <div [class]="step.stepClass">
                  <div class="restock-item-step-dot"></div>
                  <div class="restock-item-step-label">{{ step.label | translate }}</div>
                </div>
              }
            </div>
            @if (currentOrder.productLine) {
              <tidy-row class="vertical-align-top extra-bottom-padding">
                <tidy-image
                  [src]="'assets/img/packages.svg'"
                  class="title-size" />
                @if (currentOrder.productUrl) {
                  <tidy-text
                    [actionBody]="currentOrder.productLine"
                    [translate]="false"
                    (action)="openProductUrl()"
                    class="link" />
                } @else {
                  <tidy-text
                    [body]="currentOrder.productLine"
                    [translate]="false" />
                }
              </tidy-row>
            }
            @if (currentOrder.quantityLine) {
              <tidy-row class="vertical-align-top extra-bottom-padding">
                <tidy-image
                  [src]="'assets/img/best-price.svg'"
                  class="title-size" />
                <tidy-text
                  [body]="currentOrder.quantityLine"
                  [translate]="false" />
              </tidy-row>
            }
            @if (currentOrder.substitutionNote) {
              <tidy-alert class="restock-setup-alert">
                <tidy-row class="vertical-align-top">
                  <tidy-image
                    [src]="'assets/img/information.svg'"
                    class="title-size" />
                  <tidy-text
                    [body]="currentOrder.substitutionNote"
                    [translate]="false" />
                </tidy-row>
              </tidy-alert>
            }
            @if (currentOrder.etaText) {
              <tidy-row class="vertical-align-top">
                <tidy-image
                  [src]="'assets/img/calendar.svg'"
                  class="title-size" />
                <tidy-text
                  [body]="currentOrder.etaText"
                  [translate]="false" />
              </tidy-row>
            }
          </tidy-card-button>
        </tidy-card>
      }
    @if (showTasksDropdown) {
      <tidy-card>
        <tidy-row class="vertical-align-center extra-bottom-padding">
          <tidy-image
            [src]="'assets/img/tool-box.svg'"
            class="header-size" />
          <tidy-text
            [header]="'Maintenance'" />
        </tidy-row>
        @if (tasksService.tasksLoaded && tasksService.linkedTasks.length === 0) {
          <tidy-row class="extra-bottom-padding">
            <tidy-text
              [body]="'None linked'" />
          </tidy-row>
        }
        @if (!tasksService.tasksLoaded) {
          <tidy-row class="extra-bottom-padding">
            <ion-skeleton-text
              animated
              style="width: 160px; height: 13px" />
          </tidy-row>
        }
        <div class="item-maintenance-tasks">
          @for (task of tasksService.linkedTasks; track task) {
            <tidy-pill-button
              [image]="'assets/svg/construct-outline.svg'"
              [text]="task.title"
              (buttonClick)="tasksService.goToTask(task.id)" />
          }
          <tidy-task-dropdown
            [priorPage]="'item-details'"
            [priorPageParams]="{itemId: item?.id, addressId: addressId}"
            [itemId]="item?.id"
            [addressId]="addressId" />
        </div>
      </tidy-card>
    }
    <tidy-card>
      <tidy-row class="vertical-align-center extra-bottom-padding">
        <tidy-image
          [src]="'assets/img/notes.svg'"
          class="header-size" />
        <tidy-text
          [header]="'Notes'" />
      </tidy-row>
      @if (notes.length === 0) {
        <tidy-row class="extra-bottom-padding">
          <tidy-text
            [helper]="'Add warranty, receipt, repair note, and anything!'" />
        </tidy-row>
      }
      @if (notes.length > 0) {
        <div style="display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px;">
          @for (note of notes; track note.id) {
            <tidy-alert>
              <tidy-row [alignLastItemRight]="true" class="extra-bottom-padding">
                <tidy-text [body]="note.user" />
                <tidy-text [helper]="note.created_at | date:'MMM d, h:mm a'" />
              </tidy-row>
              @if (note.type === 'text') {
                <tidy-row>
                  <tidy-text [body]="note.content" />
                </tidy-row>
              }
              @if (note.type === 'photo') {
                <tidy-row>
                  <img [src]="note.content" style="max-width: 200px; border-radius: 4px;" />
                </tidy-row>
              }
              @if (note.type === 'file') {
                <tidy-row class="vertical-align-center">
                  <tidy-image
                    [src]="'assets/svg/document-outline.svg'"
                    class="body-size" />
                  <tidy-text [body]="note.fileName" class="link" />
                </tidy-row>
              }
            </tidy-alert>
          }
        </div>
      }
      <form [formGroup]="noteForm">
        <tidy-message-input
          formControlName="note"
          [placeholder]="'Add a note...'"
          [form]="noteForm.controls.note"
          [rightIconAction]="boundSendNote"
          [leftIconAction]="boundSendAttachment"
          [isSendingMessage]="isSendingNote"
          class="secondary-background" />
      </form>
    </tidy-card>
    <!-- Custom fields on items are hidden until the server can save their values (a backlog idea).
    @if (customFields.length > 0) {
      <tidy-card>
        <tidy-row class="extra-bottom-padding" [alignLastItemRight]="true">
          <tidy-text [header]="'Custom Fields'" />
          <tidy-text
            [actionBody]="'Edit'"
            (action)="editCustomFields()"
            class="link" />
        </tidy-row>
        @for (field of customFields; track field) {
          <tidy-row>
            <tidy-text [body]="field?.name + ': ' + field?.value || 'N/A'" />
          </tidy-row>
        }
      </tidy-card>
    }
    @if (customFields.length === 0) {
      <tidy-card>
        <tidy-row class="extra-bottom-padding">
          <tidy-text [header]="'Custom Fields'" />
        </tidy-row>
        <tidy-row class="extra-bottom-padding">
          <tidy-text [body]="'No custom fields added.'" />
        </tidy-row>
        <div class="item-maintenance-tasks">
          <tidy-pill-button
            [image]="'assets/svg/add-outline.svg'"
            [text]="'Create Custom Field'"
            (buttonClick)="addCustomField()" />
        </div>
      </tidy-card>
    }
    -->
    <!--<maintenances-cards [upcomingMaintenances]="upcomingMaintenances" [pastMaintenances]="pastMaintenances" [addressId]="addressId"></maintenances-cards>-->
    @if (item?.change_history?.length > 0) {
      <tidy-row>
        <tidy-text
          [header]="'Change History'" />
      </tidy-row>
      @for (history of quantityChanges; track history.key) {
        <tidy-card>
          <tidy-row>
            <tidy-text
              [header]="history.title"
              [translate]="false" />
          </tidy-row>
          <tidy-row>
            <tidy-text
              [body]="history.subtitle"
              [translate]="false" />
          </tidy-row>
        </tidy-card>
      }
      @if (!hasQuantityChange) {
        <tidy-alert>
          <tidy-row>
            <tidy-text
              [body]="'No quantity changes'" />
          </tidy-row>
        </tidy-alert>
      }
    }
    @if (errorMessage) {
      <tidy-error-message
        [text]="errorMessage" />
    }
    @if (isRightSideContent) {
      <tidy-row [align]="'center'">
        <tidy-text
          class="link red"
          [body]="'Delete Item'"
          (action)="deleteItem()" />
      </tidy-row>
    }
  }

    </tidy-wrapper>
  </ng-template>

  @if (!isRightSideContent) {
    <app-scroll-wrapper [templateContent]="content" />
          }

          @if (isRightSideContent) {
            <ion-content class="ion-content-right-column">
              <ng-container *ngTemplateOutlet="content" />
            </ion-content>
          }
